import {
  FC,
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useApi } from '@backstage/core-plugin-api';
import { mgaApiRef, ConversationAssistant } from '../api/MgaApi';

interface FavoritesContextType {
  favorites: ConversationAssistant[];
  favoriteIds: Set<string>;
  isLoading: boolean;
  error: Error | undefined;
  favoriteAssistant: (assistant: ConversationAssistant) => Promise<void>;
  unfavoriteAssistant: (id: string) => Promise<void>;
  isAssistantFavorited: (id: string) => boolean;
  refetchFavorites: () => void;
}

export const FavoritesContext = createContext<FavoritesContextType | undefined>(
  undefined,
);

export const FavoritesProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const mgaApi = useApi(mgaApiRef);
  const { mutate } = useSWRConfig();
  const swrKey = 'favorite-assistants';

  const {
    data: favorites = [],
    error,
    isLoading,
  } = useSWR<ConversationAssistant[]>(swrKey, () =>
    mgaApi.getFavoriteAssistants(),
  );

  const favoriteIds = useMemo(
    () => new Set(favorites.map(fav => fav.id)),
    [favorites],
  );

  const favoriteAssistant = useCallback(
    async (assistant: ConversationAssistant) => {
      const newFavorite = {
        ...assistant,
        favoritedAt: new Date().toISOString(),
      };
      const optimisticData = [...favorites, newFavorite];

      await mutate(swrKey, optimisticData, false);
      try {
        await mgaApi.favoriteAssistant(assistant);
        mutate(swrKey);
      } catch (e) {
        mutate(swrKey, favorites, false);
        throw e;
      }
    },
    [favorites, mgaApi, mutate, swrKey],
  );

  const unfavoriteAssistant = useCallback(
    async (id: string) => {
      const optimisticData = favorites.filter(fav => fav.id !== id);

      await mutate(swrKey, optimisticData, false);
      try {
        await mgaApi.unfavoriteAssistant(id);
        mutate(swrKey);
      } catch (e) {
        mutate(swrKey, favorites, false);
        throw e;
      }
    },
    [favorites, mgaApi, mutate, swrKey],
  );

  const isAssistantFavorited = useCallback(
    (id: string) => favoriteIds.has(id),
    [favoriteIds],
  );

  const refetchFavorites = useCallback(() => mutate(swrKey), [mutate, swrKey]);

  const contextValue: FavoritesContextType = {
    favorites,
    favoriteIds,
    isLoading,
    error,
    favoriteAssistant,
    unfavoriteAssistant,
    isAssistantFavorited,
    refetchFavorites,
  };

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context)
    throw new Error('useFavorites must be used within a FavoritesProvider');
  return context;
};
